import type { ArmorType, Loadout, WeaponClass, WeaponStats } from './types';

export const WEAPONS = {
  awp: { damage: 30, fireTicks: 130, accuracy: 4 / 5, critChance: 1 / 5, critMult: 1.6, accuracyBag: { hits: 4, size: 5 }, critBag: { crits: 1, size: 5 } },
  rifle: { damage: 11, fireTicks: 45, accuracy: 3 / 4, critChance: 1 / 6, critMult: 1.5, accuracyBag: { hits: 3, size: 4 }, critBag: { crits: 1, size: 6 } },
  lmg: { damage: 5.5, fireTicks: 19, accuracy: 5 / 8, critChance: 1 / 16, critMult: 1.4, accuracyBag: { hits: 5, size: 8 }, critBag: { crits: 1, size: 16 } },
} as const satisfies Record<WeaponClass, WeaponStats>;

export const BUDGET = 100;
export const WEAPON_COST = { awp: 60, rifle: 40, lmg: 30 } as const satisfies Record<WeaponClass, number>;
export const GRENADE_COST = 15;
export const SMOKE_COST = 10;
export const FLASH_COST = 12;
export const FLASH_ACCURACY = 0.5;
export const FLASH_SPLASH_MAX = 2;
export const MEDKIT_COST = 50;
export const BASE_HP = 120;

export const ARMOR = {
  none: { cost: 0, points: 0 },
  light: { cost: 22, points: 40 },
  heavy: { cost: 45, points: 80 },
} as const satisfies Record<ArmorType, { cost: number; points: number }>;
export const ARMOR_HP_REDUCTION = 0.3;
export const ARMOR_DEGRADE = 0.3;
export const ARMOR_DEGRADE_FLAT = 16;
export const ARMOR_CRIT_REDUCE = 0;
export const CRIT_ARMOR_PIERCE = 0.4;

// WHY: bump COMBAT_RULES_VERSION on any combat-formula change — the provably-fair verifier replays each match against its
// ruleset
export const COMBAT_RULES_VERSION = 30;

export const ADRENALINE_PER_GAP = 0.15;
export const ADRENALINE_MAX_FRAC = 0.6;
export const COMEBACK_HP_FLOOR = 0.55;
export const FOCUS_RESIST_K = 0.18;
export const FOCUS_RESIST_CAP = 0.52;

export type Stance = 'attack' | 'defense' | 'maneuver';
export const STANCE = {
  attack: { outgoing: 1.4, mitigation: 1.0, evasion: 1.0, reflect: 0 },
  defense: { outgoing: 0.78, mitigation: 0.72, evasion: 1.0, reflect: 0.37 },
  maneuver: { outgoing: 1.0, mitigation: 1.0, evasion: 0.85, reflect: 0 },
} as const satisfies Record<Stance, { outgoing: number; mitigation: number; evasion: number; reflect: number }>;

export const KNIFE = {
  damage: 31,
  weak: 20,
  executeHpFrac: 0.27,
  punishMult: 1.15,
  tick: 30,
} as const;

export function loadoutCost(l: Loadout): number {
  return (
    WEAPON_COST[l.weapon] +
    ARMOR[l.armor].cost +
    (l.grenade ? GRENADE_COST : 0) +
    (l.smoke ? SMOKE_COST : 0) +
    (l.medkit ? MEDKIT_COST : 0) +
    (l.flash ? FLASH_COST : 0)
  );
}

export function maxHpOf(_l: Loadout): number {
  return BASE_HP;
}
export function armorPointsOf(l: Loadout): number {
  return ARMOR[l.armor].points;
}

export const STARTING_LOADOUT: Loadout = {
  weapon: 'rifle', armor: 'none', grenade: false, smoke: false, medkit: false, flash: false,
};

const ARCHETYPES: Loadout[] = [
  { weapon: 'awp', armor: 'heavy', grenade: false, smoke: true, medkit: false, flash: false },
  { weapon: 'rifle', armor: 'heavy', grenade: true, smoke: false, medkit: false, flash: false },
  { weapon: 'lmg', armor: 'heavy', grenade: true, smoke: false, medkit: true, flash: false },
];

export function demoTeam(size: number, offset: number): Loadout[] {
  return Array.from({ length: size }, (_, i) => ARCHETYPES[(i + offset) % ARCHETYPES.length]);
}
